import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import { Button, BhiFormDialog, TextInput } from 'src/UIKit/components';

import { ShortLink } from '../../types';
import { CopyToClipboardButton } from '../../../../components/CopyToClipboardButton';
import { formatShortUrl } from '../../helpers';
import { useExpirationDate } from '../../hooks';
import { ExpirationDateForm } from 'src/components/ExpirationDateForm';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useShortLinkApi } from '../../api/ShortLinksApi';
import { normalizeUrl, validateUrl } from '../../../../constants/helpers';

interface EditShortLinkModalProps {
  open: boolean;
  onClose: () => void;
  // Edit mode props
  onSave?: (linkId: string, updates: { url?: string; expiresAt?: string }) => Promise<void>;
  shortLink?: ShortLink | null;
  // Create mode props
  mode?: 'edit' | 'create';
  onCreate?: (shortUrl: string) => void;
  // Focus management
  disablePortal?: boolean;
  // Context-specific props
  isLinkTrackingPage?: boolean;
}

export const EditShortLinkModal: React.FC<EditShortLinkModalProps> = ({
  open,
  onClose,
  onSave,
  shortLink,
  mode = 'edit',
  onCreate,
  disablePortal = false,
  isLinkTrackingPage = false,
}) => {
  const expirationState = useExpirationDate();
  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [urlTouched, setUrlTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyId = useSelector(getCurrentCompanyId);
  const { createShortLink } = useShortLinkApi();

  const isCreateMode = mode === 'create' || !shortLink;

  // Determine button text and title based on context
  const buttonText = isLinkTrackingPage ? 'Create' : 'Insert';
  const modalTitle = isLinkTrackingPage ? `${shortLink ? 'Edit' : 'Create'} Link to Track` : 'Insert Link to Track';
  const loadingText = isCreateMode ? `${buttonText}ing...` : 'Saving...';
  const saveText = isCreateMode ? buttonText : 'Save Changes';

  // Initialize form with current values when modal opens
  useEffect(() => {
    if (!open) {
      return;
    }
    if (isCreateMode) {
      expirationState.resetExpiration();
      setUrl('');
      setUrlTouched(false);
      setError(null);
      return;
    }
    if (shortLink) {
      expirationState.initializeFromDate(shortLink.expiresAt);
      setUrl(shortLink.url);
      setUrlTouched(false);
      setError(null);
    }
  }, [open, shortLink, expirationState.initializeFromDate, expirationState.resetExpiration, isCreateMode]);

  const isUrlChanged = useMemo(() => !!(shortLink && url !== shortLink.url), [shortLink, url]);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(event.target.value);
    if (!urlTouched) {
      setUrlTouched(true);
    }
  };

  const handleSave = async () => {
    setError(null);

    // Validate expiration date
    if (!expirationState.validateExpirationDate()) {
      return;
    }

    // Create mode flow
    if (isCreateMode) {
      const normalized = normalizeUrl(url);
      if (!normalized) {
        setError('Please enter a URL');
        return;
      }
      if (!validateUrl(normalized)) {
        setError('Please enter a valid URL');
        return;
      }

      try {
        setIsLoading(true);
        const requestData = {
          url: normalized,
          expiresAt: expirationState.getFormattedDate(),
        } as { url: string; expiresAt: string | null };
        const response = await createShortLink(companyId!, requestData);
        const shortUrlCode = response.data.code;
        const fullShortUrl = formatShortUrl(shortUrlCode);
        if (onCreate) {
          onCreate(fullShortUrl);
        }
        onClose();
        expirationState.resetExpiration();
        setUrl('');
        setUrlTouched(false);
      } catch (err: unknown) {
        // Log and show UI error
        console.error('Error creating short URL:', err);
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError?.response?.data?.message || 'Failed to create short URL');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Edit mode flow
    if (!shortLink) return;

    // Validate URL if changed
    if (isUrlChanged && !validateUrl(url)) {
      setError('Please enter a valid URL');
      return;
    }

    const normalizedUrl = isUrlChanged ? normalizeUrl(url) : url;
    const updates: { url: string; expiresAt?: string } = { url: normalizedUrl };

    const expiresAtValue = expirationState.getFormattedDate() || undefined;
    if (expiresAtValue !== shortLink.expiresAt) {
      updates.expiresAt = expiresAtValue;
    }

    // Save the updates directly
    await performSave(updates);
  };

  const performSave = async (updates: { url?: string; expiresAt?: string }) => {
    if (!shortLink || !onSave) return;

    try {
      setIsLoading(true);
      await onSave(shortLink.id, updates);
      onClose();
    } catch (err) {
      console.error('Error updating short link:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message || 'Failed to update short URL');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form state
    expirationState.resetExpiration();
    setUrlTouched(false);
  };

  const isExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const hasChanges =
    isUrlChanged || (expirationState.getFormattedDate() || undefined) !== shortLink?.expiresAt;

  const shortUrl = shortLink ? formatShortUrl(shortLink.code) : '';

  return (
    <BhiFormDialog
      open={open}
      onClose={handleClose}
      title={modalTitle}
      disablePortal={disablePortal}
      error={error}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              isCreateMode
                ? isLoading || !url.trim() || !validateUrl(url) ||
                  (expirationState.hasExpiration && !expirationState.expirationDate)
                : isLoading || !hasChanges || (urlTouched && !validateUrl(url))
            }
          >
            {isLoading ? loadingText : saveText}
          </Button>
        </>
      }
    >
      {!isCreateMode && shortLink && (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Short URL:</strong>
            </Typography>
            <CopyToClipboardButton
              text={shortUrl}
              showToast
              successMessage="Short URL copied to clipboard!"
            >
              <Typography variant="body2" color="text.secondary">
                {shortUrl}
              </Typography>
            </CopyToClipboardButton>
          </Stack>
          {isExpired(shortLink.expiresAt) && (
            <Alert severity="warning">This link is currently expired</Alert>
          )}
        </Stack>
      )}

      <TextInput
        label="Target URL"
        value={url}
        onChange={handleUrlChange}
        disabled={isLoading}
        error={urlTouched && !validateUrl(url)}
        margin="none"
        helperText={
          urlTouched && !validateUrl(url)
            ? 'Please enter a valid URL'
            : isCreateMode
            ? 'Enter a valid URL with a proper domain and TLD (e.g. google.com)'
            : 'The destination URL that this short link redirects to'
        }
      />
      {!isCreateMode && isUrlChanged && (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight="bold">
            Warning: Changing the URL will affect all existing links!
          </Typography>
          <Typography variant="body2">
            All existing shared links will now redirect to the new URL. This may break
            existing integrations or campaigns.
          </Typography>
        </Alert>
      )}
      <ExpirationDateForm
        expirationState={expirationState}
        disabled={isLoading}
        showHelperText
      />
    </BhiFormDialog>
  );
};
